import type { MatchEvent } from "../../../store/useMatchLogStore";
import { formatMatchClock } from "../utils";
import {
  EXTRA_FIRST_HALF_END_SECONDS,
  EXTRA_HALF_MINUTES,
  EXTRA_SECOND_HALF_END_SECONDS,
  REGULATION_FIRST_HALF_SECONDS,
  REGULATION_SECOND_HALF_SECONDS,
  type PeriodPhase,
} from "../hooks/usePeriodManager";

export const parseTimestampSafe = (timestamp?: string | null): number => {
  if (!timestamp) return 0;
  const normalized =
    timestamp.endsWith("Z") ||
    timestamp.includes("+") ||
    timestamp.includes("-", 10)
      ? timestamp
      : `${timestamp}Z`;
  return new Date(normalized).getTime();
};

export const parseClockToSeconds = (clock?: string): number => {
  if (!clock) return 0;
  const [mm, rest] = clock.split(":");
  const seconds = parseFloat(rest || "0");
  return Number(mm) * 60 + seconds;
};

export const compareCardEventOrder = (
  left: { event: MatchEvent; index: number },
  right: { event: MatchEvent; index: number },
) => {
  const leftPeriod = Number(left.event.period || 0);
  const rightPeriod = Number(right.event.period || 0);
  if (leftPeriod !== rightPeriod) return leftPeriod - rightPeriod;

  const leftTs = Date.parse(left.event.timestamp || "");
  const rightTs = Date.parse(right.event.timestamp || "");
  const leftHasTs = Number.isFinite(leftTs);
  const rightHasTs = Number.isFinite(rightTs);
  if (leftHasTs && rightHasTs && leftTs !== rightTs) return leftTs - rightTs;

  const leftClock = parseClockToSeconds(left.event.match_clock);
  const rightClock = parseClockToSeconds(right.event.match_clock);
  if (leftClock !== rightClock) return leftClock - rightClock;

  return left.index - right.index;
};

export const compareSubstitutionEventOrder = (
  left: { event: MatchEvent; index: number },
  right: { event: MatchEvent; index: number },
) => {
  const leftPeriod = Number(left.event.period || 0);
  const rightPeriod = Number(right.event.period || 0);
  if (leftPeriod !== rightPeriod) return leftPeriod - rightPeriod;

  const leftClock = parseClockToSeconds(left.event.match_clock);
  const rightClock = parseClockToSeconds(right.event.match_clock);
  if (leftClock !== rightClock) return leftClock - rightClock;

  const leftTs = Date.parse(left.event.timestamp || left.event._saved_at || "");
  const rightTs = Date.parse(
    right.event.timestamp || right.event._saved_at || "",
  );
  const leftHasTs = Number.isFinite(leftTs);
  const rightHasTs = Number.isFinite(rightTs);
  if (leftHasTs && rightHasTs && leftTs !== rightTs) return leftTs - rightTs;

  return left.index - right.index;
};

export const formatSecondsAsClock = (seconds: number): string => {
  // Math.max(0, NaN) is NaN, not 0 — guard finiteness too, or a NaN input
  // (e.g. 0/0 upstream) renders "NaN:NaN".
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const mins = Math.floor(safeSeconds / 60);
  const secs = Math.floor(safeSeconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export const formatSecondsAsClockWithMs = (seconds: number): string => {
  const safeSeconds = Math.max(0, seconds);
  const mins = Math.floor(safeSeconds / 60);
  const wholeSeconds = Math.floor(safeSeconds % 60);
  const ms = Math.floor((safeSeconds - Math.floor(safeSeconds)) * 1000);
  return `${String(mins).padStart(2, "0")}:${String(wholeSeconds).padStart(
    2,
    "0",
  )}.${String(ms).padStart(3, "0")}`;
};

export const addMillisecondsToClock = (
  clock: string,
  deltaMs: number,
): string => {
  const match = clock.match(/^(\d+):(\d{2})(?:\.(\d{3}))?$/);
  if (!match) return clock;
  const minutes = Number(match[1]);
  const seconds = Number(match[2]);
  const milliseconds = Number(match[3] || 0);
  const totalMs = minutes * 60_000 + seconds * 1_000 + milliseconds + deltaMs;
  const safeTotal = Math.max(0, totalMs);
  const nextMinutes = Math.floor(safeTotal / 60_000);
  const remainder = safeTotal % 60_000;
  const nextSeconds = Math.floor(remainder / 1_000);
  const nextMs = remainder % 1_000;
  return `${String(nextMinutes).padStart(2, "0")}:${String(
    nextSeconds,
  ).padStart(2, "0")}.${String(nextMs).padStart(3, "0")}`;
};

const EXTRA_HALF_REGULATION_SECONDS = EXTRA_HALF_MINUTES * 60;

// Canonical start-of-period baseline for the broadcast-cap display (Section
// "Broadcast clock helper" of the clock-desync fix). Mirrors the
// `canonicalStartSeconds` switch in `usePeriodManager.getPeriodInfo`.
const BROADCAST_BASELINE_SECONDS: Partial<Record<PeriodPhase, number>> = {
  FIRST_HALF: 0,
  SECOND_HALF: REGULATION_FIRST_HALF_SECONDS,
  FIRST_HALF_EXTRA_TIME: REGULATION_SECOND_HALF_SECONDS,
  SECOND_HALF_EXTRA_TIME: EXTRA_FIRST_HALF_END_SECONDS,
};

const BROADCAST_REGULATION_SECONDS: Partial<Record<PeriodPhase, number>> = {
  FIRST_HALF: REGULATION_FIRST_HALF_SECONDS,
  SECOND_HALF: REGULATION_FIRST_HALF_SECONDS,
  FIRST_HALF_EXTRA_TIME: EXTRA_HALF_REGULATION_SECONDS,
  SECOND_HALF_EXTRA_TIME: EXTRA_HALF_REGULATION_SECONDS,
};

// Stopped/between phases show a fixed cap for the main clock. Regulation is
// over by definition here, so `periodElapsedSeconds` is repurposed as the
// added time carried over from the period that just ended (0 if the caller
// has none available). PENALTIES and COMPLETED are listed here for
// reference only — `computeBroadcastClock` below branches on both BEFORE
// ever consulting this map, since neither has a single correct fixed cap
// (see that branch's doc comment).
const BROADCAST_STOPPED_CAP_SECONDS: Partial<Record<PeriodPhase, number>> = {
  HALFTIME: REGULATION_FIRST_HALF_SECONDS,
  FULLTIME: REGULATION_SECOND_HALF_SECONDS,
  EXTRA_HALFTIME: EXTRA_FIRST_HALF_END_SECONDS,
  PENALTIES: EXTRA_SECOND_HALF_END_SECONDS,
  COMPLETED: EXTRA_SECOND_HALF_END_SECONDS,
};

export interface BroadcastClock {
  /** Absolute broadcast seconds for the main clock, capped at the period's regulation end. */
  mainSeconds: number;
  /** Stoppage/added time accrued beyond the period's regulation end. */
  addedSeconds: number;
  /** Main clock formatted as "MM:SS". */
  mainClock: string;
  /** Added time formatted as "+MM:SS", or "" when there is none. */
  addedClock: string;
}

const trimMs = (clock: string): string => clock.split(".")[0] || clock;

/**
 * Presentation-only broadcast-cap transform: the main clock caps at each
 * period's regulation end (45:00 / 90:00 / 105:00 / 120:00) and stoppage
 * time is reported separately as `addedSeconds`/`addedClock`, only summed
 * at the very end. Does not touch any backend-accumulated numeric field.
 */
export const computeBroadcastClock = (
  phase: PeriodPhase,
  periodElapsedSeconds: number,
): BroadcastClock => {
  const safeElapsed = Math.max(0, periodElapsedSeconds || 0);
  const stoppedCap = BROADCAST_STOPPED_CAP_SECONDS[phase];

  let mainSeconds: number;
  let addedSeconds: number;
  if (phase === "COMPLETED" || phase === "PENALTIES") {
    // COMPLETED is reachable from three different prior points (Fulltime at
    // 90:00 with no extra time, or SECOND_HALF_EXTRA_TIME/PENALTIES at up to
    // 120:00+shootout) — unlike every other stopped-cap phase below, which
    // each have exactly one legal prior period, so a single fixed cap can
    // never be correct here. The caller passes the match's real uncapped
    // total (e.g. `globalSeconds`) as `periodElapsedSeconds` for this phase
    // specifically; show it as-is with no separate added-time badge — the
    // match is over, so there is nothing left to "add" (client bug: a
    // 98:54 match was showing a flat 120:00).
    //
    // PENALTIES (PMA-COCK-197 v1.184.0 correction, EPIC-33 judge round-1):
    // originally had its own branch here that returned the fixed constant
    // `EXTRA_SECOND_HALF_END_SECONDS` (120:00) UNCONDITIONALLY — correct for
    // a shootout reached after extra time, but wrong the moment UC1
    // (PMA-COCK-199) added a direct Fulltime -> Penalties route: a match
    // that skipped extra time entirely still showed a flat 120:00, the
    // exact "single fixed cap can never be correct here" bug COMPLETED's
    // fix above already solved for a different phase, reintroduced for this
    // one by the new route. PMA-MATCH-030 banks (freezes) the match's real
    // elapsed total into `match_time_seconds`/`clock_seconds_at_period_start`
    // the instant PENALTIES is entered and clears
    // `current_period_start_timestamp`, so by the time this runs the
    // caller's `periodElapsedSeconds` argument for this phase (`globalTime
    // Seconds`/`globalSeconds` — see CockpitContext.tsx/
    // PublicMatchAnalytics.tsx) is ALREADY that frozen real total, not a
    // live-ticking value. Showing it as-is, uncapped, is simultaneously
    // correct for a shootout reached via extra time (~120:00+) and one
    // reached directly from regulation (~90:0x), and it does not tick
    // further — the freeze itself (PMA-MATCH-030) is unchanged, only the
    // number frozen. This also fixes shootout kicks' stamped `match_clock`:
    // `formatStampedMatchClock` derives from this same `mainSeconds`/
    // `addedSeconds` pair, so every kick logged in `useCockpitShootout.ts`
    // inherits the same correction with no separate fix needed. The
    // shootout is still not played time, so `addedSeconds` stays 0
    // unconditionally — no `+MM:SS` badge, unchanged from before.
    mainSeconds = safeElapsed;
    addedSeconds = 0;
  } else if (stoppedCap !== undefined) {
    mainSeconds = stoppedCap;
    addedSeconds = safeElapsed;
  } else {
    const baseline = BROADCAST_BASELINE_SECONDS[phase] ?? 0;
    const regDuration =
      BROADCAST_REGULATION_SECONDS[phase] ?? REGULATION_FIRST_HALF_SECONDS;
    mainSeconds = baseline + Math.min(safeElapsed, regDuration);
    addedSeconds = Math.max(0, safeElapsed - regDuration);
  }

  return {
    mainSeconds,
    addedSeconds,
    mainClock: trimMs(formatMatchClock(mainSeconds)),
    addedClock:
      addedSeconds > 0 ? `+${trimMs(formatMatchClock(addedSeconds))}` : "",
  };
};

/**
 * Selects the `periodElapsedSeconds` argument `computeBroadcastClock` should
 * be called with, for the cases its caller has to choose between
 * (PMA-COCK-202/EPIC-37, extracted from `CockpitContext.tsx`/
 * `PublicMatchAnalytics.tsx`'s previously-inline, previously-duplicated
 * ternary — a single shared implementation so the two surfaces cannot
 * silently diverge the way an inline copy-pasted ternary could).
 *
 * - `PENALTIES`/`COMPLETED`: the pure EFFECTIVE total
 *   (`Math.round(effectiveTime)`), NOT `globalTimeSeconds`/`globalSeconds`
 *   (`effectiveTime + ineffectiveSeconds + varSeconds`). PMA-MATCH-030 (and,
 *   for a genuinely finished match, the equivalent completion freeze) only
 *   bank the effective component into `clock_seconds_at_period_start`/
 *   `match_time_seconds`; feeding the global total was PMA-COCK-197
 *   v1.184.0's latent bug for PENALTIES (see that entry's own v1.185.0
 *   correction) and PMA-COCK-202's own confirmed-but-deliberately-unfixed
 *   sibling defect for COMPLETED (folded in here, EPIC-39/PMA-COCK-206) —
 *   invisible in every prior test for either phase because they all used
 *   `ineffective_time_seconds: 0`, where the two totals are numerically
 *   identical. Rounded so a fractional `effectiveTime` (only reachable
 *   transiently on the cockpit side, where it comes from `useMatchTimer`'s
 *   own async tick-effect state — never on the public page, where it is a
 *   synchronous `useMemo` of the same `match` object that determines
 *   `currentPhase`) can never make the two surfaces disagree at a
 *   `formatMatchClock` (floor, not round) boundary.
 * - Any other (live) phase: `lastLiveAddedSeconds`, the just-ended live
 *   period's added time, unchanged from before this extraction.
 */
export const resolveBroadcastArgument = (
  currentPhase: PeriodPhase,
  effectiveTime: number,
  // Unused now that COMPLETED has been folded into the PENALTIES branch
  // below (EPIC-39/PMA-COCK-206) — underscore-prefixed only to satisfy
  // `noUnusedParameters`. Kept in the signature, at the same position, so
  // both call sites' existing positional arguments (`globalTimeSeconds`/
  // `globalSeconds`) stay unchanged, and so a future phase that needs the
  // ineffective-inclusive total has an obvious place to read it from again.
  _globalOrGlobalTimeSeconds: number,
  lastLiveAddedSeconds: number,
): number => {
  if (currentPhase === "PENALTIES" || currentPhase === "COMPLETED") {
    return Math.round(effectiveTime);
  }
  return lastLiveAddedSeconds;
};

/**
 * The `match_clock` value to STAMP onto a new event at logging time (Bug 3,
 * "stop the ongoing data loss going forward" — team_plan.md). Deliberately
 * different from the cockpit's own on-screen display, which keeps the
 * broadcast mainSeconds/addedSeconds split ("90:00" + a separate "+3:00" —
 * the correct convention for a LIVE match, unchanged by this function). The
 * STORED value instead preserves the full uncapped elapsed
 * (mainSeconds + addedSeconds, e.g. "93:00") so a later stoppage-time event
 * is never indistinguishable from every other event flattened onto the same
 * cap — exactly the defect already-stored data needs retroactive
 * reconstruction for (`reconstructStoppageElapsedSeconds` above,
 * `analytics-public-contract.md` PMA-PUB-011). `formatMatchClock`'s "MM:SS"
 * shape (no cap on minute digits) still satisfies the backend's
 * `match_clock` regex `^\d{1,3}:\d{2}(\.\d{1,3})?$` for any realistic
 * elapsed value.
 *
 * Call sites: `CockpitContext.tsx`'s `broadcastMatchClock` — fed to
 * `useActionFlow`/`useCockpitInteractionHandlers`/
 * `useCockpitSubstitutionFlow`/`useIneffectiveTime` for event stamping
 * ONLY. It is never rendered on screen — the on-screen clock reads
 * `broadcastClock.mainClock`/`.addedClock` directly, a separate value,
 * intentionally unaffected by this function.
 */
export const formatStampedMatchClock = (
  broadcastClock: BroadcastClock,
): string =>
  formatMatchClock(broadcastClock.mainSeconds + broadcastClock.addedSeconds);

// Numeric MatchEvent.period -> PeriodPhase. Mirrors `PHASE_BY_PERIOD_KEY` in
// `PublicMatchTimeline.tsx` (that one is keyed by the string "1".."4" used
// to index `period_timestamps`; this one is keyed by the numeric
// MatchEvent.period field events are actually stamped with). Period 5
// (Penalties) has no regulation-minute concept and is intentionally absent.
const PERIOD_PHASE_BY_NUMBER: Partial<Record<number, PeriodPhase>> = {
  1: "FIRST_HALF",
  2: "SECOND_HALF",
  3: "FIRST_HALF_EXTRA_TIME",
  4: "SECOND_HALF_EXTRA_TIME",
};

type PeriodTimestamps = Record<
  string,
  { start?: string; end?: string; global_start_seconds?: number }
>;

interface StoppageReconstructionEvent {
  timestamp?: string | null;
  period?: number;
  match_clock?: string | null;
}

/**
 * Retroactive reconstruction for an event whose persisted `match_clock` was
 * flattened to the period's exact regulation cap (e.g. "90:00") by the
 * pre-fix broadcast-clock stamping bug (PMA-COCK-* client bug wave,
 * 2026-08-14) — collapsing every stoppage-time event (90+1, 90+2, 90+3...)
 * onto the same value. Returns `null` (caller keeps the parsed match_clock
 * unchanged) whenever match_clock is NOT sitting exactly on the cap, since
 * that is either normal in-play data (already accurate) or already-uncapped
 * data from the going-forward stamping fix (also already accurate) —
 * reconstruction only kicks in for the genuinely ambiguous case.
 *
 * Derives elapsed seconds from `event.timestamp` minus the period's
 * persisted wall-clock start (`period_timestamps[period].start`) — the same
 * anchor `buildBoundaryRows` (`PublicMatchTimeline.tsx`) already trusts, not
 * a derived/guessed start.
 *
 * Basis, confirmed against the actual live call sites (not assumed — see
 * team_findings.md "fe-clock — answering the lead's outstanding questions"
 * and the lead's own trace, 2026-08-14): the LIVE broadcast clock's
 * `periodElapsedSeconds` is a GLOBAL-clock delta —
 * `usePeriodManager.ts:127-144` `computePeriodElapsedSeconds` returns
 * `globalTimeSeconds - period_timestamps[period].global_start_seconds`,
 * and `useMatchTimer.ts:167-170`'s `globalSeconds = effectiveTime +
 * ineffectiveSeconds + timeoutSeconds + currentVarSeconds` ticks 1:1 with
 * wall time through EFFECTIVE, INEFFECTIVE, timeout, and VAR alike — so a
 * plain wall-clock delta (what this function computes) matches the global
 * delta the operator's own clock was showing, for all of those.
 *
 * **Accepted residual bound, not corrected:** the one case where global and
 * wall-clock diverge is a genuine full clock STOP (`current_period_start_
 * timestamp` set to `null` — the `handleGlobalClockStop` path, distinct
 * from an EFFECTIVE/INEFFECTIVE mode toggle) — `useMatchTimer.ts`'s timer
 * loop freezes `currentEffectiveSeconds`/`currentIneffectiveSeconds` both
 * when that timestamp is absent, so `globalSeconds` stops advancing
 * entirely while wall time keeps moving. If a full stop occurred anywhere
 * in a period, this function's wall-clock delta overstates elapsed by the
 * stop's duration for any event reconstructed after it. This is accepted,
 * not fixed, because: (1) reconstruction only ever fires on the
 * capped-sentinel branch — i.e. stoppage-time events specifically, where by
 * definition the operator kept play (and the clock) running to produce the
 * added time in the first place; a full stop landing inside that exact
 * window is not a realistic operator action; (2) `period_timestamps` +
 * `event.timestamp` carry no record of clock-stop windows at all, so
 * detecting this case retroactively is impossible without a full event
 * replay — ruled out for this fix (see team_findings.md) as disproportionate
 * risk for a minute-granularity display. See the pinned test below for the
 * accepted-but-not-corrected behavior in this scenario.
 */
export const reconstructStoppageElapsedSeconds = (
  event: StoppageReconstructionEvent,
  periodTimestamps: PeriodTimestamps | undefined,
): number | null => {
  const phase =
    event.period !== undefined
      ? PERIOD_PHASE_BY_NUMBER[event.period]
      : undefined;
  if (!phase) return null;

  const regDuration = BROADCAST_REGULATION_SECONDS[phase];
  if (regDuration === undefined) return null;
  const baseline = BROADCAST_BASELINE_SECONDS[phase] ?? 0;
  const capSeconds = baseline + regDuration;

  const persistedSeconds = parseClockToSeconds(event.match_clock ?? undefined);
  if (persistedSeconds !== capSeconds) return null; // not the ambiguous case

  const periodEntry = periodTimestamps?.[String(event.period)];
  const periodStart = periodEntry?.start;
  if (!periodStart || !event.timestamp) return null;

  // PMA-COCK-156, PROD INCIDENT: `should_start` re-stamps this SAME period's
  // `.start` on a mid-period clock restart without clearing a stale `.end`
  // left over from the prior stop — when that leaves `.end` before `.start`,
  // `.start` no longer anchors the period's true kickoff, and deriving
  // elapsed time from it would silently produce a wrong number rather than
  // the real stoppage-time value. Bail out (caller keeps the parsed capped
  // match_clock) instead of fabricating a value from a known-bad anchor.
  // Residual, NOT fully closed by this guard: a restart followed by a LATER
  // genuine stop leaves `.end > .start` again (coherent) while `.start`
  // still isn't the period's true kickoff — undetectable from
  // `period_timestamps` alone, same class of accepted-but-not-corrected gap
  // as the full-clock-stop case documented above.
  if (periodEntry?.end) {
    const staleEndMs = parseTimestampSafe(periodEntry.end);
    const ownStartMs = parseTimestampSafe(periodStart);
    if (
      Number.isFinite(staleEndMs) &&
      Number.isFinite(ownStartMs) &&
      staleEndMs <= ownStartMs
    ) {
      return null;
    }
  }

  const startMs = parseTimestampSafe(periodStart);
  const eventMs = parseTimestampSafe(event.timestamp);
  if (!Number.isFinite(startMs) || !Number.isFinite(eventMs)) return null;

  const wallElapsed = Math.max(0, (eventMs - startMs) / 1000);
  return baseline + wallElapsed;
};

export interface EventMinuteSplit {
  baseMinutes: number;
  addedMinutes: number;
}

/**
 * Splits an uncapped elapsed-seconds value into a base regulation minute and
 * a stoppage-time "added" minute count, e.g. 5580s (93:00) in the second
 * half -> `{ baseMinutes: 90, addedMinutes: 3 }`, ready for
 * `formatMatchTime` (`src/lib/utils.ts`) to render as `"90+3'"`. Both are
 * floor-divided, matching the existing no-`+1` minute convention (see the
 * comment on `formatMinuteFromSeconds` in `PublicMatchTimeline.tsx`). Falls
 * back to a plain floor-divided minute with no added component when
 * `period` has no regulation mapping (e.g. penalties, or an unset period).
 */
export const splitElapsedSecondsIntoMinutes = (
  elapsedSeconds: number,
  period?: number,
): EventMinuteSplit => {
  const safeElapsed = Math.max(0, elapsedSeconds || 0);
  const phase =
    period !== undefined ? PERIOD_PHASE_BY_NUMBER[period] : undefined;
  const regDuration = phase ? BROADCAST_REGULATION_SECONDS[phase] : undefined;

  if (regDuration === undefined) {
    return { baseMinutes: Math.floor(safeElapsed / 60), addedMinutes: 0 };
  }

  const baseline = BROADCAST_BASELINE_SECONDS[phase as PeriodPhase] ?? 0;
  const capSeconds = baseline + regDuration;
  return {
    baseMinutes: Math.floor(Math.min(safeElapsed, capSeconds) / 60),
    addedMinutes: Math.floor(Math.max(0, safeElapsed - capSeconds) / 60),
  };
};

export type HalftimeWindowKind = "halftime" | "extraTimeHalftime";

const HALFTIME_WINDOW_PAIRS: Array<{
  endKey: string;
  startKey: string;
  kind: HalftimeWindowKind;
}> = [
  { endKey: "1", startKey: "2", kind: "halftime" },
  { endKey: "3", startKey: "4", kind: "extraTimeHalftime" },
];

/**
 * Retroactive halftime-break membership test for a single event timestamp
 * (Bug 4 — a substitution logged during the break rendering as a computed
 * minute, e.g. "48'", instead of "Descanso"). `event.period` CANNOT
 * identify a halftime event: the backend maps HALFTIME to period 1
 * (`_status_period()`, `matches_new.py`), so a break-time substitution is
 * indistinguishable from a genuine period-1 event by `period` alone.
 *
 * Compares `event.timestamp` (wall clock) directly against the persisted
 * `period_timestamps[endKey].end` / `[startKey].start` window — no
 * arithmetic beyond a range check, so this carries none of the
 * wall-clock-subtraction risk `reconstructStoppageElapsedSeconds` above
 * documents. If the next period hasn't started yet (still mid-break, e.g. a
 * live match), the window is treated as open-ended.
 *
 * Both boundaries are STRICT (`>`/`<`, not `>=`/`<=`): `period_timestamps[
 * key].end`/`.start` are stamped from the same backend transition that stops
 * or resumes the clock (`matches_new.py`'s `should_stop`/`should_start`
 * handling — `.end = now` at the moment of the transition itself), so an
 * event landing in the exact same instant as that transition was logged
 * while the period was still live (e.g. a card at the literal 45:00 whistle
 * with zero stoppage), not during the break.
 *
 * **Incoherent-window guard (PMA-COCK-156, PROD INCIDENT):** `should_start`
 * (`matches_new.py`) re-stamps `period_timestamps[endKey].start` on every
 * clock restart WITHOUT clearing a stale `.end` left over from a prior
 * mid-period stop, so `.end` can land BEFORE the period's own current
 * `.start` — a stop-then-restart artifact, not a real period boundary.
 * Treating that stale `.end` as genuine made every event for the rest of
 * the match look like it fell inside an open-ended halftime window (no
 * `[startKey].start` yet to close it). A pair is only trusted as a real
 * boundary when `endKey`'s own `.end` is strictly after `endKey`'s own
 * `.start` — otherwise it is skipped, exactly like a missing `.end` above.
 *
 * `skipEndKeys` (PMA-COCK-173, optional — default behavior unchanged when
 * omitted): a caller-supplied set of `endKey`s to treat as having no window
 * at all, regardless of how coherent `.end`/`.start` otherwise look. Covers
 * a DIFFERENT incoherence than the guard above: a period whose `.end` is
 * genuinely after its own `.start` (a real mid-period stop) but which is
 * demonstrably live and running again (a clock-mode resume that never
 * cleared `period_timestamps`) — the caller (`PublicMatchTimeline.tsx`'s
 * `resolvePublicEventTiming`) computes this from `matchStatus`/
 * `current_period_start_timestamp` via `isPeriodLiveAndRunning`, since this
 * function has no access to either.
 */
export const findHalftimeWindowForTimestamp = (
  timestamp: string | null | undefined,
  periodTimestamps: PeriodTimestamps | undefined,
  skipEndKeys?: ReadonlySet<string>,
): HalftimeWindowKind | null => {
  if (!timestamp || !periodTimestamps) return null;
  const eventMs = parseTimestampSafe(timestamp);
  if (!Number.isFinite(eventMs)) return null;

  for (const { endKey, startKey, kind } of HALFTIME_WINDOW_PAIRS) {
    if (skipEndKeys?.has(endKey)) continue;
    const endStr = periodTimestamps[endKey]?.end;
    if (!endStr) continue;
    const endMs = parseTimestampSafe(endStr);
    if (!Number.isFinite(endMs) || eventMs <= endMs) continue;

    const ownStartStr = periodTimestamps[endKey]?.start;
    if (ownStartStr) {
      const ownStartMs = parseTimestampSafe(ownStartStr);
      if (Number.isFinite(ownStartMs) && endMs <= ownStartMs) continue;
    }

    const nextStartStr = periodTimestamps[startKey]?.start;
    const upperBoundMs = nextStartStr ? parseTimestampSafe(nextStartStr) : NaN;
    const hasValidUpperBound = Number.isFinite(upperBoundMs);
    if (!hasValidUpperBound || eventMs < upperBoundMs) {
      return kind;
    }
  }

  return null;
};

export const getActiveYellowCountForPlayer = (
  events: MatchEvent[],
  playerId: string,
): number => {
  let yellow = 0;
  let red = 0;

  events
    .map((event, index) => ({ event, index }))
    .filter(
      ({ event }) => event.type === "Card" && event.player_id === playerId,
    )
    .sort(compareCardEventOrder)
    .forEach(({ event }) => {
      const cardType = String(event.data?.card_type || "").toLowerCase();

      if (cardType.includes("cancel")) {
        if (red > 0 && yellow >= 2) {
          red -= 1;
          yellow -= 1;
        } else if (red > 0) {
          red -= 1;
        } else if (yellow > 0) {
          yellow -= 1;
        }
        return;
      }

      if (cardType.includes("yellow")) {
        yellow += 1;
        return;
      }

      if (cardType.includes("red")) {
        red += 1;
      }
    });

  return yellow;
};
